import { fromJS, get, List, Map } from 'immutable';
import { capitalize } from 'lodash-es';

/* Define static properties of various objects that are available as bindings */

const STATIC_FORM_BINDINGS = [
  { label: 'name', boost: 20 },
  { label: 'slug', boost: 20 },
  { label: 'anonymous' },
  { label: 'description' },
  { label: 'status' },
  { label: 'type' },
  { label: 'createdAt', boost: -20 },
  { label: 'createdBy', boost: -20 },
  { label: 'updatedAt', boost: -20 },
  { label: 'updatedBy', boost: -20 },
];

const STATIC_KAPP_BINDINGS = [
  { label: 'name', boost: 20 },
  { label: 'slug', boost: 20 },
];

const STATIC_SPACE_BINDINGS = [
  { label: 'name', boost: 20 },
  { label: 'slug', boost: 20 },
];

const STATIC_SUBMISSION_BINDINGS = [
  { label: 'id', boost: 20 },
  { label: 'coreState' },
  { label: 'currentPage' },
  { label: 'handle' },
  { label: 'type' },
  { label: 'closedAt', boost: -20 },
  { label: 'closedBy', boost: -20 },
  { label: 'createdAt', boost: -20 },
  { label: 'createdBy', boost: -20 },
  { label: 'submittedAt', boost: -20 },
  { label: 'submittedBy', boost: -20 },
  { label: 'updatedAt', boost: -20 },
  { label: 'updatedBy', boost: -20 },
];

const STATIC_IDENTITY_BINDINGS = [
  { label: 'username', boost: 20 },
  { label: 'displayName', boost: 10 },
  { label: 'email', boost: 10 },
  { label: 'anonymous' },
  { label: 'authenticated' },
  { label: 'sessionToken' },
  { label: 'spaceAdmin' },
];

/**
 * Builds code editor bindings from the provided options
 *
 * @param {object} options
 *
 * @param {object} [options.values] Options for values bindings
 * @param {string} [options.values.label=form]
 * @param {object[]} options.values.data
 * @param {object} options.values.data[].name
 * @param {object} options.values.data[].renderType
 *
 * @param {object} [options.form] Options for form bindings
 * @param {string} [options.form.label=form]
 * @param {object[]} [options.form.staticBindings]
 * @param {object[]} [options.form.attributeDefinitions]
 *
 * @param {object} [options.kapp] Options for kapp bindings
 * @param {string} [options.kapp.label=kapp]
 * @param {object[]} [options.kapp.staticBindings]
 * @param {object[]} [options.kapp.attributeDefinitions]
 *
 * @param {object} [options.space] Options for space bindings
 * @param {string} [options.space.label=space]
 * @param {object[]} [options.space.staticBindings]
 * @param {object[]} [options.space.attributeDefinitions]
 *
 * @param {object} [options.submission] Options for submission bindings
 * @param {string} [options.submission.label=submission]
 * @param {object[]} [options.submission.staticBindings]
 *
 * @param {object} [options.identity] Options for identity bindings
 * @param {string} [options.identity.label=identity]
 * @param {object[]} [options.identity.staticBindings]
 * @param {object[]} [options.identity.attributeDefinitions]
 * @param {object[]} [options.identity.profileAttributeDefinitions]
 *
 * @param {object} [options.resources] Options for bridged resource bindings
 * @param {string} [options.resources.label=resources]
 * @param {string} [options.resources.bridgedResourceName]
 * @param {string[]} [options.resources.attributes]
 *
 * @param {object[]} [options.staticBindings] Additional bindings to include
 *
 * @returns The bindings object to be used by the CodeEditor component.
 */
export const buildCodeEditorBindings = ({
  values,
  form,
  kapp,
  space,
  submission,
  identity,
  resources,
  staticBindings,
} = {}) => {
  return List(
    [
      values &&
        buildValuesBindings(values.label || 'values', List(values.data)),
      form &&
        buildObjectWithAttributesBindings(
          form.label || 'form',
          List(form.staticBindings).concat(STATIC_FORM_BINDINGS),
          List(form.attributeDefinitions),
        ),
      kapp &&
        buildObjectWithAttributesBindings(
          kapp.label || 'kapp',
          List(kapp.staticBindings).concat(STATIC_KAPP_BINDINGS),
          List(kapp.attributeDefinitions),
        ),
      space &&
        buildObjectWithAttributesBindings(
          space.label || 'space',
          List(space.staticBindings).concat(STATIC_SPACE_BINDINGS),
          List(space.attributeDefinitions),
        ),
      submission &&
        buildObjectWithAttributesBindings(
          submission.label || 'submission',
          List(submission.staticBindings).concat(STATIC_SUBMISSION_BINDINGS),
        ),
      identity &&
        buildObjectWithAttributesBindings(
          identity.label || 'identity',
          List(identity.staticBindings).concat(STATIC_IDENTITY_BINDINGS),
          List(identity.attributeDefinitions),
          List(identity.profileAttributeDefinitions),
        ),
      resources &&
        buildResourcesBindings(
          resources.label || 'resources',
          resources.bridgedResourceName,
          resources.attributes,
        ),
      ...(staticBindings || []).map(binding => fromJS(binding)),
    ].filter(Boolean),
  );
};

// Builds code editor binding for kinetic objects with optional attribute
// definitions
const buildObjectWithAttributesBindings = (
  label,
  staticBindings = List(),
  attributeDefinitions,
  profileAttributeDefinitions,
) => {
  return Map({
    label,
    type: 'function',
    children: staticBindings.concat(
      [
        attributeDefinitions &&
          Map({
            label: 'attribute',
            siblings: buildAttributeBindings(attributeDefinitions),
            detail: 'Attribute Selector',
            collapseSiblings: true,
          }),
        profileAttributeDefinitions &&
          Map({
            label: 'profileAttribute',
            siblings: buildAttributeBindings(profileAttributeDefinitions, true),
            detail: 'Profile Attribute Selector',
            collapseSiblings: true,
          }),
      ].filter(Boolean),
    ),
  });
};

// Converts a list of attribute definitions to code editor bindings
const buildAttributeBindings = (definitions = List(), isProfile) =>
  definitions.map(def =>
    Map({
      label: get(def, 'name'),
      detail: `${isProfile ? 'Profile ' : ''}Attribute${
        get(def, 'allowsMultiple') ? ' (Multiple)' : ''
      }`,
      section: `${isProfile ? 'Profile ' : ''}Attributes`,
    }),
  );

// Converts a list of field data objects to code editor bindings
const buildValuesBindings = (label, data = List()) =>
  Map({
    label,
    type: 'function',
    children: data.map(field =>
      Map({
        label: get(field, 'name'),
        detail: capitalize(get(field, 'renderType')),
      }),
    ),
  });

// Converts bridged resources data to code editor bindings
const buildResourcesBindings = (label, bridgedResourceName, attributes) =>
  Map({
    label,
    type: 'function',
    children: [
      Map({
        label: bridgedResourceName,
        siblings: attributes.map(label =>
          Map({
            label,
            section: `${bridgedResourceName} Bridged Resource`,
          }),
        ),
        detail: 'Bridged Resource',
        collapseSiblings: true,
      }),
    ],
  });
